import React, { useState } from 'react';
import { X, Shield, Calendar, User, FileText, AlertTriangle, Clock } from 'lucide-react';
import { getTodayAEST } from '../../utils/dateUtils';

interface Vehicle {
  id: string;
  registration: string;
  type: string;
  make?: string;
  model?: string;
}

interface VehicleInspection {
  id: string;
  vehicle_id: string;
  inspection_type: string;
  inspection_date: string;
  expiry_date: string;
  inspector_name?: string;
  inspector_company?: string;
  certificate_number?: string;
  status: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface InspectionFormProps {
  vehicle: Vehicle;
  inspection?: VehicleInspection | null;
  onSubmit: (inspectionData: Partial<VehicleInspection>) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
}

export const InspectionForm: React.FC<InspectionFormProps> = ({ 
  vehicle,
  inspection, 
  onSubmit, 
  onClose, 
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    vehicle_id: vehicle.id,
    inspection_type: inspection?.inspection_type || 'nhvr',
    inspection_date: inspection?.inspection_date || getTodayAEST(),
    town: inspection?.town || '',
    notes: inspection?.notes || ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Calculate status based on expiry date
    const today = new Date(getTodayAEST());
    const expiryDate = new Date(formData.expiry_date);
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    let status = 'current';
    if (daysUntilExpiry < 0) {
      status = 'overdue';
    } else if (daysUntilExpiry <= 30) {
      status = 'due_soon';
    }

    const inspectionData = {
      ...formData,
      expiry_date: expiryDate.toISOString().split('T')[0],
      status
    };

    await onSubmit(inspectionData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Auto-calculate expiry date based on inspection type and date
  const handleInspectionDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inspectionDate = e.target.value;
    setFormData(prev => ({ ...prev, inspection_date: inspectionDate }));
    
    if (inspectionDate && formData.inspection_type) {
      const date = new Date(inspectionDate);
      let expiryDate = new Date(date);
      
      // Set expiry based on inspection type
      switch (formData.inspection_type) {
        case 'nhvr':
          expiryDate.setFullYear(date.getFullYear() + 1); // 12 months
          break;
        case 'roadworthy':
          expiryDate.setFullYear(date.getFullYear() + 1); // 12 months
          break;
        case 'service':
          expiryDate.setMonth(date.getMonth() + 6); // 6 months
          break;
        case 'safety':
          expiryDate.setFullYear(date.getFullYear() + 1); // 12 months
          break;
        default:
          expiryDate.setFullYear(date.getFullYear() + 1); // Default 12 months
      }
      
      setFormData(prev => ({ 
        ...prev, 
        inspection_date: inspectionDate
      }));
    }
  };

  const handleInspectionTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const inspectionType = e.target.value;
    setFormData(prev => ({ ...prev, inspection_type: inspectionType }));
    
    // Auto-update expiry date if inspection date is set
    if (formData.inspection_date) {
      const date = new Date(formData.inspection_date);
      let expiryDate = new Date(date);
      
      switch (inspectionType) {
        case 'nhvr':
          expiryDate.setFullYear(date.getFullYear() + 1);
          break;
        case 'roadworthy':
          expiryDate.setFullYear(date.getFullYear() + 1);
          break;
        case 'service':
          expiryDate.setMonth(date.getMonth() + 6);
          break;
        case 'safety':
          expiryDate.setFullYear(date.getFullYear() + 1);
          break;
        default:
          expiryDate.setFullYear(date.getFullYear() + 1);
      }
      
      setFormData(prev => ({ 
        ...prev, 
        inspection_type: inspectionType
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Shield className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {inspection ? 'Edit Inspection' : 'Add Vehicle Inspection'}
              </h3>
              <p className="text-sm text-gray-500">{vehicle.registration} - {vehicle.type}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Vehicle Information Display */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Vehicle Information</h4>
            <div className="text-sm text-blue-800 space-y-1">
              <p><strong>Registration:</strong> {vehicle.registration}</p>
              <p><strong>Type:</strong> {vehicle.type.charAt(0).toUpperCase() + vehicle.type.slice(1)}</p>
              {vehicle.make && vehicle.model && (
                <p><strong>Vehicle:</strong> {vehicle.make} {vehicle.model}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Inspection Type *
              </label>
              <select
                name="inspection_type"
                required
                value={formData.inspection_type}
                onChange={handleInspectionTypeChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="nhvr">NHVR Inspection</option>
                <option value="roadworthy">Roadworthy Certificate</option>
                <option value="service">Service Inspection</option>
                <option value="safety">Safety Inspection</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline h-4 w-4 mr-1" />
                Inspection Date *
              </label>
              <input
                type="date"
                name="inspection_date"
                required
                value={formData.inspection_date}
                onChange={handleInspectionDateChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Inspection Town/Location
            </label>
            <input
              type="text"
              name="town"
              value={formData.town}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Sydney, Melbourne, Brisbane..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              rows={3}
              value={formData.notes}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Additional notes about the inspection..."
            />
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Saving...' : (inspection ? 'Update Inspection' : 'Add Inspection')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};